import React, { FunctionComponent } from 'react';
import withStyles from 'react-jss';
import { Grid, Button, Icon, Container } from 'semantic-ui-react';
import Capitalize from 'capitalize';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import { path } from 'ramda';

import { ClassProp, WithRouterProps } from 'helpers/types';
import { Theme } from 'config';
import { CSVPlaceholder } from 'components/Placeholder/presets';
import PollingQuery from 'components/PollingQuery';
import { formatDate } from 'helpers/date';
import {
  ExportJobStatus,
  downloadS3File,
} from 'scenes/CSVExport/CSVExport.helper';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';

import GET_EXPORT_JOB_STATUS from './CSVStatusChecker.queries';

const styles = (theme: Theme) => ({
  container: {
    padding: '4rem',
    margin: '8rem 0 4rem 0',
    backgroundColor: '#fff',
    borderRadius: '0.25em',
    boxShadow:
      '0 0.0625em 0 0 rgba(0,0,0,0.03), 0 0.125em 0.375em 0 rgba(0,0,0,0.06);',
  },
  label: {
    color: `${theme.violet} !important`,
    fontWeight: 'bold',
  },
  value: {
    color: `#767676 !important`,
  },
  button: {
    color: `#fff !important`,
    backgroundColor: `${theme.violet} !important`,
  },
});

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = WithRouterProps & {
  classes: Styles;
};

type QueryResponse = {
  exportJobStatus: ExportJobStatus;
};

const formatJobType = (jobType = '') =>
  Capitalize.words(jobType.replace(/_/g, ' '));

const getPeriod = (startDate: string, endDate: string) => (
  <>
    <span>{formatDate(startDate, 'MMM DD, YYYY')} - </span>
    <span>{formatDate(endDate, 'MMM DD, YYYY')}</span>
  </>
);

const renderCSVStatusChecker = (classes: Styles, isInternalUser: boolean) => ({
  exportJobStatus,
}: QueryResponse) => {
  const {
    uuid,
    s3Filename,
    exportJobType,
    createdAt,
    merchantName,
    venueName,
    nextExecutionParameters,
  } = exportJobStatus;

  const {
    startDate,
    endDate,
    cardNetwork,
    cardTransactionType,
    cardFulfillmentType,
    runForDateType,
  } = nextExecutionParameters;

  const handleClick = () => {
    downloadS3File(s3Filename);
  };

  return (
    <Container className={classes.container}>
      <Grid>
        <Grid.Row>
          <Grid.Column width={16} className={classes.label}>
            File ID
          </Grid.Column>

          <Grid.Column width={16} className={classes.value}>
            {uuid}
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16} className={classes.label}>
            Export Job Type
          </Grid.Column>

          <Grid.Column width={16} className={classes.value}>
            {formatJobType(exportJobType)}
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16} className={classes.label}>
            Created At
          </Grid.Column>

          <Grid.Column width={16} className={classes.value}>
            {formatDate(createdAt)}
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16} className={classes.label}>
            Parameters
          </Grid.Column>

          {merchantName && isInternalUser && (
            <Grid.Column width={16} className={classes.value}>
              Merchant: {merchantName}
            </Grid.Column>
          )}

          {venueName && (
            <Grid.Column width={16} className={classes.value}>
              Venue: {venueName}
            </Grid.Column>
          )}

          <Grid.Column width={16} className={classes.value}>
            {getPeriod(startDate as string, endDate as string)}
          </Grid.Column>

          {cardNetwork && (
            <Grid.Column width={16} className={classes.value}>
              Card Network: {cardNetwork}
            </Grid.Column>
          )}

          {cardTransactionType && (
            <Grid.Column width={16} className={classes.value}>
              Transaction Type: {cardTransactionType}
            </Grid.Column>
          )}

          {cardFulfillmentType && (
            <Grid.Column width={16} className={classes.value}>
              Fulfillment Type: {cardFulfillmentType}
            </Grid.Column>
          )}
          {runForDateType && (
            <Grid.Column width={16} className={classes.value}>
              Run For Date Type: {runForDateType}
            </Grid.Column>
          )}
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <Button
              as="a"
              target="__blank"
              className={classes.button}
              onClick={handleClick}
              icon
            >
              <Icon name="download" /> Download
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Container>
  );
};

const CSVStatusChecker: FunctionComponent<Props> = ({ classes, match }) => {
  const params = { uuid: path(['params', 'uuid'], match) as string };
  const { isInternalUser } = useUserMgmt();

  if (params.uuid) {
    return (
      <PollingQuery
        pollInterval={5000}
        pathCondition={['exportJobStatus', 'jobCompleted']}
        query={GET_EXPORT_JOB_STATUS}
        PlaceholderLoading={CSVPlaceholder}
        render={renderCSVStatusChecker(classes, isInternalUser)}
        variables={{ params }}
      />
    );
  }

  return null;
};

export default compose<Props, any>(
  withRouter,
  withStyles(styles),
)(CSVStatusChecker);
